import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
    Aangifte, AangifteRapport, BokkzAlgDataService, IncomeTaxEntity,
    JaarrekeningVenW,
    OmschrijvingEnBedrag,
    Onderneming, Ondernemingsvorm, SpecificatiePriveGebruikAuto
} from '@snelstart-ventures/fiscaal/domain-logic';
import { BehaviorSubject, filter, Subscription } from 'rxjs';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { BokkzIbDataService } from '@snelstart-ventures/fiscaal/domain-logic';
import { Event, NavigationEnd, Router } from '@angular/router';
import { SharedUiButtonModule } from '@snelstart/ui-button';
import { SharedUiDetailsModule } from '@snelstart/ui-details';
import { SharedUiFormControlsModule } from '@snelstart/ui-form-controls';
import { CommonModule } from '@angular/common';

@Component({
    selector: 'snelstart-venture-fiscaal-feature-samenwerkingsverband',
    templateUrl: './samenwerkingsverband.component.html',
    styleUrls: ['./samenwerkingsverband.component.scss'],
    imports: [
        SharedUiButtonModule,
        ReactiveFormsModule,
        SharedUiDetailsModule,
        SharedUiFormControlsModule,
        CommonModule
    ]
})
export class SamenwerkingsverbandComponent implements OnInit, OnDestroy {
    private subscriptions = new Subscription();
    @Input() public id!: string;
    public clientId!: string;

    private focusedElementId: string | null = null;
    private routerSubscription: Subscription | undefined;
    private incomeTaxEntitySubscription: Subscription | undefined;
    private clientEntitySubscription: Subscription | undefined;
    private isDefAangifteSubscription: Subscription | undefined;

    private isSaving = false;

    public incomeTaxEntity = {} as IncomeTaxEntity;

    public partnerBoolean: boolean = false;
    @Input() partner = new BehaviorSubject<boolean>(false);

    public isDefAangifte: boolean = false;
    public showSpinner = true;
    public ondernemingFG = {} as FormGroup;

    public winstResultatenrekeningFg = {} as FormGroup;
    public specJaarrekeningFg = {} as FormGroup;
    public winstResultatenrekening = 0;
    public rapport = {} as AangifteRapport;

    constructor(private fb: FormBuilder, private router: Router, public dataService: BokkzIbDataService, private dataAlgService: BokkzAlgDataService) {
        this.ondernemingFG = this.fb.group({
            ondernemingen: this.fb.array([
                this.createOndernemingFormGroup()])
        });
        this.winstResultatenrekeningFg = this.fb.group({
            winstResultatenrekening: null as unknown as number,
        });
    }

    formStructure: any;

    //onderneming
    get ondernemingen(): FormArray {
        return this.ondernemingFG.get('ondernemingen') as FormArray;
    }

    createOndernemingFormGroup(onderneming?: Onderneming) {
        return this.dataService.createOndernemingFormGroup(onderneming);
    }


    public ngOnInit(): void {
        this.isDefAangifteSubscription = this.dataService.isDefAangifte.subscribe((isDefAangifte) => {
            this.isDefAangifte = isDefAangifte;
        });

        // Partner subscription BUITEN incomeTaxEntity subscription
        this.subscriptions.add(
            this.partner.subscribe((boolean) => {
                this.partnerBoolean = boolean;
                this.ondernemingFG.reset();
                this.updateForm();
            })
        );

        this.incomeTaxEntitySubscription = this.dataService.incomeTaxEntity.subscribe((incomeTaxEntity) => {
            this.incomeTaxEntity = incomeTaxEntity;
            if (incomeTaxEntity.aangifte) {
                this.updateForm();
            }
        });

        this.dataService.winstResultatenrekening.subscribe(value => {
            this.winstResultatenrekening = value;
            this.winstResultatenrekeningFg.patchValue({ winstResultatenrekening: value });
        });

        this.clientEntitySubscription = this.dataAlgService.client.subscribe((client) => {
            this.clientId = client.sstRelationId!;
        });

        this.routerSubscription = this.router.events
            .pipe(filter((event: Event): event is NavigationEnd => event instanceof NavigationEnd))
            .subscribe((event: NavigationEnd) => {
                this.handleUrlChange(event.urlAfterRedirects);
            });
    }

    ngOnDestroy(): void {
        if (this.routerSubscription) {
            this.routerSubscription.unsubscribe(); // Unsubscribe to avoid memory leaks
        }
        if (this.incomeTaxEntitySubscription) {
            this.incomeTaxEntitySubscription.unsubscribe();
        }
        if (this.clientEntitySubscription) {
            this.clientEntitySubscription.unsubscribe();
        }
        if (this.isDefAangifteSubscription) {
            this.isDefAangifteSubscription.unsubscribe();
        }
    }

    handleUrlChange(newUrl: string) {
        if (!this.isSaving && this.hasUnsavedChanges()) {
            this.isSaving = true;
            this.dataService.postIncomeTaxEntity(this.clientId, false, this.incomeTaxEntity);
            this.isSaving = false;
        }
    }

    private hasUnsavedChanges(): boolean {
        return this.ondernemingFG.dirty;
    }


    private updateForm(): void {
        let ondernemingenLijst = this.incomeTaxEntity.aangifte?.box1?.winstUitOnderneming?.ondernemingen || [];
        if (this.partnerBoolean && this.incomeTaxEntity.aangifte?.partnerAangifte) {
            ondernemingenLijst = this.incomeTaxEntity.aangifte?.partnerAangifte?.box1?.winstUitOnderneming?.ondernemingen || [];
        }

        const formArray = this.ondernemingFG.get('ondernemingen') as FormArray;
        const focusedControl = this.focusedElementId ? document.getElementById(this.focusedElementId) as HTMLElement : null;

        // Verwijder overtollige form groups
        while (formArray.length > ondernemingenLijst.length) {
            formArray.removeAt(formArray.length - 1);
        }

        // Update of voeg ondernemingen toe
        for (let i = 0; i < ondernemingenLijst.length; i++) {
            if (i < formArray.length) {
                const control = formArray.at(i) as FormGroup;
                control.patchValue(ondernemingenLijst[i]);
            } else {
                const formGroup = this.createOndernemingFormGroup(ondernemingenLijst[i]);
                formArray.push(formGroup);
            }
            this.updateTotaalResultaat(i);
        }

        // Herstel focus
        if (focusedControl && document.contains(focusedControl)) {
            focusedControl.focus();
        }
    }

    saveForm(): void {
        this.ondernemingFG.updateValueAndValidity();
        if (!this.partnerBoolean) {
            this.incomeTaxEntity.aangifte = {
                ...this.incomeTaxEntity.aangifte,
                box1: {
                    ...this.incomeTaxEntity.aangifte?.box1,
                    winstUitOnderneming: {
                        ...this.incomeTaxEntity.aangifte?.box1?.winstUitOnderneming,
                        ondernemingen: this.ondernemingFG.getRawValue().ondernemingen
                    }
                }
            } as Aangifte;
            this.dataService.updateIncomeTaxEntity(this.incomeTaxEntity);

        } else {
            this.incomeTaxEntity.aangifte = {
                ...this.incomeTaxEntity.aangifte,
                partnerAangifte: {
                    ...this.incomeTaxEntity.aangifte?.partnerAangifte,
                    box1: {
                        ...this.incomeTaxEntity.aangifte?.partnerAangifte?.box1,
                        winstUitOnderneming: {
                            ...this.incomeTaxEntity.aangifte?.partnerAangifte?.box1?.winstUitOnderneming,
                            ondernemingen: this.ondernemingFG.getRawValue().ondernemingen
                        }
                    }
                },
            } as Aangifte;
            this.dataService.updateIncomeTaxEntity(this.incomeTaxEntity);
        }
    }

    private getRelevantAangifte(): Aangifte | undefined {
        return this.partnerBoolean
            ? this.incomeTaxEntity.aangifte?.partnerAangifte
            : this.incomeTaxEntity.aangifte;
    }

    private getOndernemingen(): Onderneming[] | undefined {
        const aangifte = this.getRelevantAangifte();
        return aangifte?.box1?.winstUitOnderneming?.ondernemingen;
    }

    public heeftOndernemingen(): boolean {
        const ondernemingen = this.getOndernemingen();
        return !!ondernemingen && ondernemingen.length > 0;
    }

    public heeftInvullenOpPersoonlijkNiveau(): boolean {
        const ondernemingen = this.getOndernemingen();
        return !!ondernemingen && ondernemingen[0].invullenOpPersoonlijkNiveau != undefined && ondernemingen[0].invullenOpPersoonlijkNiveau;
    }

    public heeftSamenwerkingsverband(): boolean {
        const ondernemingen = this.getOndernemingen();
        return !!ondernemingen?.[0]?.ondernemingsvorm &&
            ondernemingen[0].ondernemingsvorm !== Ondernemingsvorm.EENMANSZAAK;
    }

    updateTotaalResultaat(index: number): void {
        const onderneming = this.ondernemingen.at(index) as FormGroup;
        const resultaatSamenwerking = onderneming.get('resultaatSamenwerking') as FormGroup;

        const vergoedingArbeid = resultaatSamenwerking.get('vergoedingVoorArbeidBelastingplichtige')?.value || 0;
        const overigeVergoedingen = resultaatSamenwerking.get('overigeVergoedingenBelastingplichtige')?.value || 0;
        const aandeelWinst = resultaatSamenwerking.get('aandeelRestantWinstBelastingplichtige')?.value || 0;

        const totaal = vergoedingArbeid + overigeVergoedingen + aandeelWinst;

        resultaatSamenwerking.get('totaalResultaat')?.patchValue(totaal, { emitEvent: false });
    }
}
